import { useCallback, useEffect, useState } from 'react';

import { Tooltip } from '@material-ui/core';

import {
  RiDeleteBinLine,
  RiDeleteColumn,
  RiDeleteRow,
  RiInsertColumnLeft,
  RiInsertColumnRight,
  RiInsertRowBottom,
  RiInsertRowTop,
} from '@remixicon/react';
import type { ResolvedPos } from '@tiptap/pm/model';
import { BubbleMenu } from '@tiptap/react/menus';

import { ButtonIcon, Flex } from '@backstage/ui';

import { CommentZoneProvider } from '@spotify/plugin-confidence-comments-react';

import { useBlockZoneId } from '../../hooks';
import { generateBlockId } from '../../utils';
import { BlockReorderButtons } from '../BlockReorderButtons';
import { BlockCommentButton } from '../Comment';
import { useCurrentEditor } from '../Context';
import { ToolbarCard } from '../FloatingCard';

const findTableInfo = (state: { selection: { $from: ResolvedPos } }) => {
  const { $from } = state.selection;
  let depth = $from.depth;
  while (depth > 0) {
    if ($from.node(depth).type.name === 'table') {
      return { pos: $from.before(depth), node: $from.node(depth) };
    }
    depth--;
  }
  return null;
};

const TableToolbarContent = () => {
  const { editor } = useCurrentEditor();
  const [tableBlockId, setTableBlockId] = useState<string | null>(null);

  // Assign a blockId if the table doesn't have one yet
  useEffect(() => {
    if (!editor) return undefined;

    const recomputeTableBlockId = () => {
      const info = findTableInfo(editor.state);
      if (!info) {
        setTableBlockId(null);
        return;
      }

      const { pos, node } = info;

      if (!node.attrs.blockId) {
        const blockId = generateBlockId();
        const { tr } = editor.state;
        tr.setNodeAttribute(pos, 'blockId', blockId);
        editor.view.dispatch(tr);
        setTableBlockId(blockId);
        return;
      }

      setTableBlockId(node.attrs.blockId as string);
    };

    recomputeTableBlockId();
    editor.on('selectionUpdate', recomputeTableBlockId);
    editor.on('transaction', recomputeTableBlockId);

    return () => {
      editor.off('selectionUpdate', recomputeTableBlockId);
      editor.off('transaction', recomputeTableBlockId);
    };
  }, [editor]);

  const zoneId = useBlockZoneId('table', tableBlockId);

  const handleAddColumnBefore = useCallback(() => {
    editor?.chain().focus().addColumnBefore().run();
  }, [editor]);

  const handleAddColumnAfter = useCallback(() => {
    editor?.chain().focus().addColumnAfter().run();
  }, [editor]);

  const handleDeleteColumn = useCallback(() => {
    editor?.chain().focus().deleteColumn().run();
  }, [editor]);

  const handleAddRowBefore = useCallback(() => {
    editor?.chain().focus().addRowBefore().run();
  }, [editor]);

  const handleAddRowAfter = useCallback(() => {
    editor?.chain().focus().addRowAfter().run();
  }, [editor]);

  const handleDeleteRow = useCallback(() => {
    editor?.chain().focus().deleteRow().run();
  }, [editor]);

  const handleDeleteTable = useCallback(() => {
    if (!editor) return;
    const info = findTableInfo(editor.state);
    if (!info) return;
    const { pos, node } = info;
    editor
      .chain()
      .focus()
      .deleteRange({ from: pos, to: pos + node.nodeSize })
      .run();
  }, [editor]);

  const toolbar = (
    <ToolbarCard p="0.5">
      <Flex gap="0.5">
        <BlockReorderButtons />
        <Tooltip title="Insert column left">
          <ButtonIcon
            aria-label="Insert column left"
            onClick={handleAddColumnBefore}
            size="small"
            icon={<RiInsertColumnLeft />}
            variant="tertiary"
          />
        </Tooltip>
        <Tooltip title="Insert column right">
          <ButtonIcon
            aria-label="Insert column right"
            onClick={handleAddColumnAfter}
            size="small"
            icon={<RiInsertColumnRight />}
            variant="tertiary"
          />
        </Tooltip>
        <Tooltip title="Delete column">
          <ButtonIcon
            aria-label="Delete column"
            onClick={handleDeleteColumn}
            size="small"
            icon={<RiDeleteColumn />}
            variant="tertiary"
          />
        </Tooltip>
        <Tooltip title="Insert row above">
          <ButtonIcon
            aria-label="Insert row above"
            onClick={handleAddRowBefore}
            size="small"
            icon={<RiInsertRowTop />}
            variant="tertiary"
          />
        </Tooltip>
        <Tooltip title="Insert row below">
          <ButtonIcon
            aria-label="Insert row below"
            onClick={handleAddRowAfter}
            size="small"
            icon={<RiInsertRowBottom />}
            variant="tertiary"
          />
        </Tooltip>
        <Tooltip title="Delete row">
          <ButtonIcon
            aria-label="Delete row"
            onClick={handleDeleteRow}
            size="small"
            icon={<RiDeleteRow />}
            variant="tertiary"
          />
        </Tooltip>
        {zoneId && <BlockCommentButton />}
        <Tooltip title="Delete table">
          <ButtonIcon
            aria-label="Delete table"
            onClick={handleDeleteTable}
            size="small"
            icon={<RiDeleteBinLine />}
            variant="tertiary"
          />
        </Tooltip>
      </Flex>
    </ToolbarCard>
  );

  return zoneId ? (
    <CommentZoneProvider zone={zoneId}>{toolbar}</CommentZoneProvider>
  ) : (
    toolbar
  );
};

export const TableToolbar = () => {
  const { editor } = useCurrentEditor();

  const getReferencedVirtualElement = useCallback(() => {
    if (!editor) return null;
    const info = findTableInfo(editor.state);
    if (!info) return null;
    const tableDOM = editor.view.nodeDOM(info.pos) as HTMLElement;
    if (!tableDOM) return null;
    return {
      getBoundingClientRect: () => tableDOM.getBoundingClientRect(),
    };
  }, [editor]);

  return (
    <BubbleMenu
      editor={editor ?? undefined}
      pluginKey="tableToolbar"
      getReferencedVirtualElement={getReferencedVirtualElement}
      options={{ offset: 6, placement: 'top-end' }}
      style={{ zIndex: 1000 }}
      shouldShow={({ view, state }) => {
        if (!view.hasFocus()) return false;
        return findTableInfo(state) !== null;
      }}
    >
      <TableToolbarContent />
    </BubbleMenu>
  );
};
